import React, { useState, useEffect } from "react";

const CompoundInterestCalculator = () => {
  const [principalAmount, setPrincipalAmount] = useState(100000);
  const [annualInterestRate, setAnnualInterestRate] = useState(8);
  const [timePeriod, setTimePeriod] = useState(10);
  const [inflationRate, setInflationRate] = useState(6);
  const [result, setResult] = useState({
    futureValue: "0.00",
    totalInvestment: "0.00",
    estimatedReturns: "0.00",
    inflationAdjustedValue: "0.00",
  });

  // Compound Interest Calculation Function
  const calculateCompoundInterest = () => {
    const P = parseFloat(principalAmount);
    const r = parseFloat(annualInterestRate) / 100;
    const n = parseFloat(timePeriod);
    const inflation = parseFloat(inflationRate) / 100;

    if (isNaN(P) || isNaN(r) || isNaN(n) || P <= 0 || n <= 0) return;

    // Compound Interest Formula: A = P(1 + r)^n
    const futureValue = P * Math.pow(1 + r, n);
    const totalInvestment = P;
    const estimatedReturns = futureValue - totalInvestment;
    
    // Calculate inflation-adjusted value
    const inflationAdjustedValue = futureValue / Math.pow(1 + inflation, n);

    setResult({
      futureValue: futureValue.toFixed(2),
      totalInvestment: totalInvestment.toFixed(2),
      estimatedReturns: estimatedReturns.toFixed(2),
      inflationAdjustedValue: inflationAdjustedValue.toFixed(2),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
  calculateCompoundInterest();
}, [principalAmount, annualInterestRate, timePeriod, inflationRate]);

  const growthPercentage = result.totalInvestment > 0 
    ? ((Number(result.estimatedReturns) / Number(result.totalInvestment)) * 100).toFixed(1)
    : 0;

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <h1>Compound Interest Calculator</h1>
          <p>
            Calculate the compound interest on your investments and see how your money grows over time with the power of compounding
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Investment Details
              </h2>

              {/* Principal Amount */}
              <div className="input-group">
                <label>Principal Amount</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={principalAmount}
                    onChange={(e) => setPrincipalAmount(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[50000, 100000, 500000, 1000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setPrincipalAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? (amount / 100000).toFixed(0) + 'L' : (amount / 1000).toFixed(0) + 'K'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Annual Interest Rate */}
              <div className="input-group">
                <label>Annual Interest Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={annualInterestRate}
                    onChange={(e) => setAnnualInterestRate(e.target.value)}
                    className="calc-input"
                    placeholder="8"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[5, 7, 10, 12].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setAnnualInterestRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Period */}
              <div className="input-group">
                <label>Time Period (years)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                  />
                </div>
                <div className="quick-select-group">
                  {[5, 10, 15, 20].map((years) => (
                    <button
                      key={years}
                      onClick={() => setTimePeriod(years)}
                      className="quick-select-btn success"
                    >
                      {years}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Inflation Rate */}
              <div className="input-group">
                <label>Expected Inflation Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              <button onClick={calculateCompoundInterest} className="calculate-btn">
                Calculate Compound Interest
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Investment Summary
              </h2>

              <div className="result-card">
                {/* Principal Amount */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Principal Amount</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalInvestment).toLocaleString("en-IN")}
                  </div>
                </div>

                {/* Total Interest Earned */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Interest Earned</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.estimatedReturns).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext green">
                    +{growthPercentage}% Growth
                  </div>
                </div>

                {/* Maturity Amount - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Maturity Amount</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.futureValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After {timePeriod} years at {annualInterestRate}% interest
                  </div>
                </div>

                {/* Inflation Adjusted Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Inflation Adjusted Value</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.inflationAdjustedValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Real value in today's terms
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Investment Breakdown</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Principal Amount</span>
                      <span className="breakdown-percentage">
                        {((Number(result.totalInvestment) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(Number(result.totalInvestment) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Interest Earned</span>
                      <span className="breakdown-percentage green">
                        {((Number(result.estimatedReturns) / Number(result.futureValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(Number(result.estimatedReturns) / Number(result.futureValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Compound Interest Calculator Work?</h3>
              <p>
                The compound interest calculator estimates the future value of your investment by
                considering the principal amount, annual interest rate, and time period. Unlike simple 
                interest, compound interest calculates interest on both the principal and previously 
                earned interest, allowing your money to grow exponentially over time.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Formula Used:</h4>
            <div className="formula-display">
              A = P × (1 + r)ⁿ
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">A</div>
                <div className="formula-param-value">Maturity Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Principal Amount</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Annual Interest Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Time Period in Years</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is compound interest?</h5>
              <p>
                Compound interest is the interest calculated on both the initial principal and the 
                accumulated interest from previous periods. This means you earn "interest on interest," 
                which can significantly increase your returns over time. It's often called the eighth 
                wonder of the world due to its exponential growth potential.
              </p>
            </div>

            <div className="faq-item">
              <h5>How is compound interest different from simple interest?</h5>
              <p>
                Simple interest is calculated only on the principal amount throughout the investment 
                period. Compound interest, however, is calculated on the principal plus any interest 
                already earned. This compounding effect means your money grows faster with compound 
                interest compared to simple interest, especially over longer time periods.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why should I consider inflation in compound interest calculations?</h5>
              <p>
                While compound interest helps your money grow, inflation reduces its purchasing power 
                over time. The inflation-adjusted value shows what your maturity amount will actually 
                be worth in today's terms, helping you understand the real growth of your investment 
                after accounting for rising prices.
              </p>
            </div>

            <div className="faq-item">
              <h5>What investments offer compound interest?</h5>
              <p>
                Many investment options offer compound interest including fixed deposits (FDs), 
                recurring deposits (RDs), Public Provident Fund (PPF), bonds, mutual funds, and 
                stocks (through reinvested dividends). The frequency of compounding varies - it can 
                be daily, monthly, quarterly, or annually depending on the investment type.
              </p>
            </div>

            <div className="faq-item">
              <h5>Does compounding frequency matter?</h5>
              <p>
                Yes, compounding frequency significantly impacts returns. More frequent compounding 
                (daily or monthly) results in higher returns compared to less frequent compounding 
                (quarterly or annually), even with the same annual interest rate. This is because 
                interest is calculated and added to the principal more often, accelerating growth.
              </p>
            </div>

            <div className="faq-item">
              <h5>How long should I invest to maximize compound interest benefits?</h5>
              <p>
                The longer you invest, the more powerful compound interest becomes. The exponential 
                growth effect is most significant over extended periods. Even a few extra years can 
                make a substantial difference. Financial experts often recommend investing for at 
                least 10-15 years to fully leverage the power of compounding.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompoundInterestCalculator;